import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { UserPreferences, loadPreferences, savePreferences } from '../types/preferences';

interface PreferencesContextType {
  preferences: UserPreferences;
  setPreferences: (next: UserPreferences | ((prev: UserPreferences) => UserPreferences)) => void;
  updatePreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void;
  save: () => void;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (ctx === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return ctx;
}

interface PreferencesProviderProps {
  children: ReactNode;
}

export const PreferencesProvider: React.FC<PreferencesProviderProps> = ({ children }) => {
  const [preferences, setPreferencesState] = useState<UserPreferences>(() => loadPreferences());

  useEffect(() => {
    savePreferences(preferences);
  }, [preferences]);

  const setPreferences = useCallback((next: UserPreferences | ((prev: UserPreferences) => UserPreferences)) => {
    setPreferencesState(next);
  }, []);

  const updatePreference = useCallback(<K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
    setPreferencesState(prev => ({ ...prev, [key]: value }));
  }, []);

  const value: PreferencesContextType = useMemo(
    () => ({
      preferences,
      setPreferences,
      updatePreference,
      save: () => savePreferences(preferences),
    }),
    [preferences, setPreferences, updatePreference]
  );

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};

export default PreferencesContext;
